import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft, FaUser, FaMapMarkerAlt, FaSearch, FaPhone, FaEnvelope, FaClock,
} from "react-icons/fa";
import Header from "../Components/Header";
import LoadingState from "../Components/States/LoadingState";
import Footer from "../Components/Footer";
import FamilyMemberModal from "../Components/FamilyMemberModal";
import AddressFormModal from "../Components/AddressFormModal";
import { loadStaffDiagnosticCenters, getDiagnosticSlots } from "../api/diagnostics.api";
import { getFamilyMembers } from "../api/family.api";
import { getAddresses } from "../api/address.api";
import { getCart } from "../api/cart.api";
import { getStaffId, getStaffName, getCompanyId } from "../utils/storage";
import {
  ITEM_TYPE,
  diagnosticMatchesItems,
  isMorningSlot,
  cartLineFromApi,
  formatPrice,
} from "../utils/labTests";

// ponytail: regex to pull first http(s) URL out of an address string
const extractMapUrl = (addr = "") => {
  const m = addr.match(/https?:\/\/\S+/);
  return m ? { clean: addr.replace(m[0], "").trim(), url: m[0] } : { clean: addr, url: null };
};

const toYMD = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

const nextDays = (count = 14) =>
  Array.from({ length: count }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return toYMD(d);
  });

const LabTestsBookPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const staffId = getStaffId();
  const companyId = getCompanyId();
  const staffName = getStaffName() || "Myself";

  const fromCart = Boolean(location.state?.fromCart);
  const singleItem = location.state?.item;
  const singleItemType = location.state?.itemType;

  const [itemType, setItemType] = useState(singleItemType || ITEM_TYPE.test);
  const [lineItems, setLineItems] = useState(singleItem ? [singleItem] : []);
  const [needsHomeCollection, setNeedsHomeCollection] = useState(
    singleItem ? Boolean(singleItem.homeCollection) : true
  );

  const [diagnostics, setDiagnostics] = useState([]);
  const [labSearch, setLabSearch] = useState("");
  const [selectedLab, setSelectedLab] = useState(null);
  const [serviceType, setServiceType] = useState("Center Visit");

  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [familyModalOpen, setFamilyModalOpen] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  const [dateOptions] = useState(() => nextDays(14));
  const [selectedDate, setSelectedDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [labPage, setLabPage] = useState(0);

  const LAB_PAGE_SIZE = 2;

  useEffect(() => {
    if (!fromCart && !singleItem) navigate("/lab-tests", { replace: true });
  }, [fromCart, singleItem, navigate]);

  const itemIds = useMemo(() => lineItems.map((i) => i.id), [lineItems]);

  useEffect(() => {
    if (!staffId) return;
    let cancelled = false;

    const init = async () => {
      try {
        if (fromCart) {
          try {
            const cartRes = await getCart(staffId);
            const items = (cartRes.data?.items || []).map(cartLineFromApi);
            if (!cancelled) {
              setLineItems(items);
              setItemType(ITEM_TYPE.test);
              setNeedsHomeCollection(items.some((i) => i.homeCollection));
            }
          } catch {
            if (!cancelled) setLineItems([]);
          }
        }

        const [famRes, addrRes] = await Promise.all([
          getFamilyMembers(staffId).catch(() => ({ data: { family_members: [] } })),
          getAddresses(staffId).catch(() => ({ data: { addresses: [] } })),
        ]);
        const members = famRes.data?.family_members || [];
        const addrs = addrRes.data?.addresses || [];
        if (cancelled) return;
        setFamilyMembers(members);
        setAddresses(addrs);
        const self = members.find((m) => /self/i.test(m.relation || ""));
        if (self) setSelectedMemberId(self._id);
        else if (members[0]) setSelectedMemberId(members[0]._id);
        if (addrs[0]) setSelectedAddressId(addrs[0]._id);

        try {
          const diagRes = await loadStaffDiagnosticCenters();
          const list = (diagRes.data?.data || []).map((d) => ({
            ...d,
            tests: d.tests || [],
            packages: d.packages || [],
            scans: d.scans || [],
          }));
          if (!cancelled) setDiagnostics(list);
        } catch {
          if (!cancelled) setDiagnostics([]);
        }
      } catch {
        if (!cancelled) setDiagnostics([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [staffId, companyId, fromCart]);

  const filteredLabs = useMemo(() => {
    const q = labSearch.trim().toLowerCase();
    const matchesQuery = (d) => {
      if (!q) return true;
      const hay = `${d.name || ""} ${d.address || ""} ${d.area || ""}`.toLowerCase();
      return hay.includes(q);
    };
    const matched = diagnostics.filter((d) => {
      if (itemIds.length && !diagnosticMatchesItems(d, itemIds)) return false;
      return matchesQuery(d);
    });
    if (itemIds.length && matched.length === 0) return diagnostics.filter(matchesQuery);
    return matched;
  }, [diagnostics, itemIds, labSearch]);

  useEffect(() => {
    setLabPage(0);
  }, [labSearch, filteredLabs.length]);

  const labPageCount = Math.max(1, Math.ceil(filteredLabs.length / LAB_PAGE_SIZE));
  const pagedLabs = filteredLabs.slice(labPage * LAB_PAGE_SIZE, labPage * LAB_PAGE_SIZE + LAB_PAGE_SIZE);

  const canContinue =
    Boolean(selectedMemberId) &&
    Boolean(selectedLab) &&
    Boolean(selectedDate) &&
    Boolean(selectedSlot) &&
    (serviceType !== "Home Collection" || Boolean(selectedAddressId));

  useEffect(() => {
    if (needsHomeCollection) setServiceType("Home Collection");
    else setServiceType("Center Visit");
  }, [needsHomeCollection]);

  const fetchSlots = (labId, date, type) => {
    if (!labId || !date) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    getDiagnosticSlots(labId, date, type)
      .then((res) => {
        const data = res.data || {};
        const list = data.slots
          || (type === "Home Collection" ? data.homeCollectionSlots : data.centerVisitSlots)
          || data.homeCollectionSlots
          || data.centerVisitSlots
          || [];
        setAvailableSlots(list.filter((s) => !s.isBooked && !s.isExpired));
      })
      .catch(() => setAvailableSlots([]))
      .finally(() => setLoadingSlots(false));
  };

  const handleDateSelect = (ymd) => {
    setSelectedDate(ymd);
    if (selectedLab) fetchSlots(selectedLab._id, ymd, serviceType);
  };

  const handleLabSelect = (lab) => {
    setSelectedLab(lab);
    setSelectedDate("");
    setSelectedSlot(null);
    setAvailableSlots([]);
  };

  useEffect(() => {
    if (selectedLab && selectedDate) fetchSlots(selectedLab._id, selectedDate, serviceType);
  }, [serviceType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFamilySaved = (member) => {
    setFamilyMembers((prev) => [...prev, member]);
    setSelectedMemberId(member._id);
    setError("");
  };

  const handleAddressSaved = (addr) => {
    setAddresses((prev) => [...prev, addr]);
    setSelectedAddressId(addr._id);
    setError("");
  };

  const handleContinue = () => {
    if (!canContinue) return;

    const patient = familyMembers.find((m) => m._id === selectedMemberId);
    const address = addresses.find((a) => a._id === selectedAddressId);

    navigate("/lab-tests/review", {
      state: {
        fromCart,
        itemType,
        lineItems,
        patient: { familyMemberId: selectedMemberId, name: patient?.fullName || staffName },
        address: serviceType === "Home Collection" ? address : null,
        diagnostic: selectedLab,
        serviceType,
        selectedDate,
        selectedSlot,
        selectedTime: selectedSlot.timeSlot,
      },
    });
  };

  if (!fromCart && !singleItem) return null;

  const subtotal = lineItems.reduce((s, i) => s + (Number(i.price) || 0), 0);
  const morning = availableSlots.filter((s) => isMorningSlot(s.timeSlot));
  const afternoon = availableSlots.filter((s) => !isMorningSlot(s.timeSlot));

  return (
    <div className="bg-surface-low min-h-screen flex flex-col">
      <Header />
      <div className="flex flex-col min-h-screen pb-16 lg:pb-0 flex-1">
        <main className="flex-1 max-w-7xl mx-auto w-full px-3 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center gap-3 mb-1">
            <button type="button" onClick={() => navigate("/lab-tests")} className="text-primary p-1" aria-label="Back">
              <FaArrowLeft size={16} />
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary">Book Diagnostics</h1>
          </div>
          <p className="text-on-surface-variant mb-6 ml-8">Choose patient, lab, and appointment time.</p>
          {error && <p className="text-error text-sm mb-4">{error}</p>}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Patient */}
              <section className="bg-surface-lowest rounded-xl border border-surface-container p-4">
                <h2 className="flex items-center gap-2 font-semibold text-primary mb-4"><FaUser /> Patient Details</h2>
                <div className="flex gap-2 overflow-x-auto pb-1 mb-3">
                  {familyMembers.map((m) => (
                    <button
                      key={m._id}
                      type="button"
                      onClick={() => setSelectedMemberId(m._id)}
                      className={`shrink-0 px-3 py-2 rounded-lg border text-xs sm:text-sm font-medium ${
                        selectedMemberId === m._id
                          ? "border-primary bg-primary-fixed/30 text-primary"
                          : "border-surface-container"
                      }`}
                    >
                      {/self/i.test(m.relation || "") ? staffName : m.fullName}
                    </button>
                  ))}
                </div>
                <button type="button" onClick={() => setFamilyModalOpen(true)} className="text-sm text-primary font-semibold">
                  + Add Dependent
                </button>
              </section>

              {/* Address */}
              {serviceType === "Home Collection" && (
                <section className="bg-surface-lowest rounded-xl border border-surface-container p-4">
                  <h2 className="flex items-center gap-2 font-semibold text-primary mb-4"><FaMapMarkerAlt /> Collection Address</h2>
                  <div className="space-y-2 mb-3">
                    {addresses.map((a) => (
                      <button
                        key={a._id}
                        type="button"
                        onClick={() => setSelectedAddressId(a._id)}
                        className={`w-full text-left p-3 rounded-lg border min-w-0 ${
                          selectedAddressId === a._id ? "border-primary bg-primary-fixed/20" : "border-surface-container"
                        }`}
                      >
                        <p className="font-medium text-sm">{a.addressType || "Address"}</p>
                        <p className="text-xs sm:text-sm text-on-surface-variant break-words">{a.street}, {a.city}, {a.state} {a.postalCode}</p>
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={() => setAddressModalOpen(true)} className="text-sm text-primary font-semibold">
                    + Add New
                  </button>
                </section>
              )}

              {/* Labs */}
              <section className="bg-surface-lowest rounded-xl border border-surface-container p-4 min-w-0">
                <h2 className="font-semibold text-primary mb-4">Available Labs</h2>
                <div className="relative mb-4">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant" />
                  <input
                    type="text"
                    placeholder="Search labs by name or area..."
                    value={labSearch}
                    onChange={(e) => setLabSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-surface-container text-sm"
                  />
                </div>
                {loading && <LoadingState branded message="Loading labs…" className="py-6" />}
                <div className="space-y-3">
                  {pagedLabs.map((lab) => (
                    <button
                      key={lab._id}
                      type="button"
                      onClick={() => handleLabSelect(lab)}
                      className={`w-full text-left p-3 sm:p-5 rounded-xl border transition min-w-0 overflow-hidden ${
                        selectedLab?._id === lab._id ? "border-primary bg-primary-fixed/20" : "border-surface-container hover:border-primary/50"
                      }`}
                    >
                      <div className="flex justify-between items-start gap-2 min-w-0">
                        <p className="font-bold text-sm sm:text-base text-on-primary-fixed-var truncate flex-1">{lab.name}</p>
                        {lab.rating != null && (
                          <span className="text-xs bg-surface-low px-2 py-0.5 rounded-full shrink-0">★ {lab.rating}</span>
                        )}
                      </div>
                      {lab.address && (() => {
                        const { clean, url } = extractMapUrl(lab.address);
                        return (
                          <p className="text-xs sm:text-sm text-on-surface-variant mt-1 break-words line-clamp-2">
                            {clean}
                            {url && (
                              <a href={url} target="_blank" rel="noopener noreferrer"
                                className="ml-1 text-primary underline"
                                onClick={(e) => e.stopPropagation()}
                              >Map</a>
                            )}
                          </p>
                        );
                      })()}
                      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-3 mt-2 text-xs text-on-surface-variant">
                        {lab.workingHours && (
                          <span className="flex items-center gap-1 min-w-0 truncate"><FaClock className="shrink-0" /> {lab.workingHours}</span>
                        )}
                        {lab.phone && (
                          <a href={`tel:${lab.phone}`} onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 min-w-0 truncate hover:text-primary">
                            <FaPhone className="shrink-0" /> {lab.phone}
                          </a>
                        )}
                        {lab.email && (
                          <a href={`mailto:${lab.email}`} onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 min-w-0 truncate hover:text-primary">
                            <FaEnvelope className="shrink-0" /> {lab.email}
                          </a>
                        )}
                      </div>
                    </button>
                  ))}
                  {!loading && !filteredLabs.length && (
                    <p className="text-sm text-on-surface-variant">No labs offer the selected items.</p>
                  )}
                </div>
                {filteredLabs.length > LAB_PAGE_SIZE && (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-surface-container">
                    <button
                      type="button"
                      onClick={() => setLabPage((p) => Math.max(0, p - 1))}
                      disabled={labPage === 0}
                      className="text-sm text-primary font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ← Previous
                    </button>
                    <span className="text-xs text-on-surface-variant">
                      Page {labPage + 1} of {labPageCount}
                    </span>
                    <button
                      type="button"
                      onClick={() => setLabPage((p) => Math.min(labPageCount - 1, p + 1))}
                      disabled={labPage >= labPageCount - 1}
                      className="text-sm text-primary font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </section>
            </div>

            {/* Sidebar */}
            <aside className="lg:sticky lg:top-4 h-fit bg-surface-lowest rounded-xl border border-surface-container p-4">
              <h2 className="font-semibold text-primary mb-4">Schedule Appointment</h2>

              {needsHomeCollection && (
                <div className="flex gap-2 mb-4">
                  {["Home Collection", "Center Visit"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setServiceType(opt)}
                      className={`flex-1 text-xs py-2 rounded-lg border font-medium ${
                        serviceType === opt ? "border-primary bg-primary-fixed/30 text-primary" : "border-surface-container"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              <p className="text-xs text-on-surface-variant mb-2">Select Date</p>
              <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
                {dateOptions.map((d) => {
                  const label = new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", day: "numeric" });
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => handleDateSelect(d)}
                      disabled={!selectedLab}
                      className={`shrink-0 px-3 py-2 rounded-lg text-xs border ${
                        selectedDate === d ? "border-primary bg-primary text-white" : "border-surface-container"
                      } disabled:opacity-40`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {loadingSlots && <LoadingState size="sm" message="Loading slots…" className="py-2 mb-2" />}
              {selectedDate && !loadingSlots && (
                <>
                  {morning.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-on-surface-variant mb-2">Morning</p>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {morning.map((s) => (
                          <button
                            key={s.timeSlot}
                            type="button"
                            onClick={() => setSelectedSlot(s)}
                            className={`text-xs py-2 rounded-lg border ${
                              selectedSlot?.timeSlot === s.timeSlot ? "border-primary bg-primary-fixed/30" : "border-surface-container"
                            }`}
                          >
                            {s.timeSlot}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {afternoon.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-on-surface-variant mb-2">Afternoon</p>
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {afternoon.map((s) => (
                          <button
                            key={s.timeSlot}
                            type="button"
                            onClick={() => setSelectedSlot(s)}
                            className={`text-xs py-2 rounded-lg border ${
                              selectedSlot?.timeSlot === s.timeSlot ? "border-primary bg-primary-fixed/30" : "border-surface-container"
                            }`}
                          >
                            {s.timeSlot}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {!availableSlots.length && <p className="text-xs text-on-surface-variant mb-4">No slots for this date.</p>}
                </>
              )}

              <div className="border-t border-surface-container pt-4 mb-4">
                <p className="text-xs text-on-surface-variant uppercase mb-1">Selected</p>
                {lineItems.slice(0, 2).map((i) => (
                  <p key={i.id} className="text-sm font-medium truncate">{i.title}</p>
                ))}
                {lineItems.length > 2 && <p className="text-xs text-on-surface-variant">+{lineItems.length - 2} more</p>}
                <p className="text-lg font-bold text-primary mt-2">{formatPrice(subtotal)}</p>
              </div>

              <button
                type="button"
                onClick={handleContinue}
                disabled={!canContinue}
                className="w-full py-3 rounded-lg bg-primary text-white font-semibold hover:bg-primary-container disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Review →
              </button>
            </aside>
          </div>
        </main>
        <Footer />
      </div>

      <FamilyMemberModal
        open={familyModalOpen}
        onClose={() => setFamilyModalOpen(false)}
        mode="create"
        member={null}
        isPrimary={false}
        staffId={staffId}
        onSaved={handleFamilySaved}
      />

      <AddressFormModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        staffId={staffId}
        existingAddresses={addresses}
        onSaved={handleAddressSaved}
      />
    </div>
  );
};

export default LabTestsBookPage;
